import { isEmpty } from 'lodash-es';
import styled from 'styled-components';

import Hero from '@klaviyo-mkt/gatsby-components/organisms/hero';
import Modal from '@klaviyo-mkt/gatsby-components/organisms/modal';
import FullColumn from '@klaviyo-mkt/gatsby-components/organisms/full-column';
import TwoColumn from '@klaviyo-mkt/gatsby-components/organisms/two-column';
import { EagerLoadingContextProvider } from '@klaviyo-mkt/gatsby-components/context/eager-loading';
import useDemoFormSettings from '../../../hooks/useDemoFormSettings';
import ComponentLoop from '../../../components/component-loop';
import { useModal } from '@klaviyo-mkt/gatsby-components/context/modal';

import type { IHero } from '@klaviyo-mkt/gatsby-components/organisms/hero/types';
import type { IFullColumn } from '@klaviyo-mkt/gatsby-components/organisms/full-column/types';
import type { ITwoColumn } from '@klaviyo-mkt/gatsby-components/organisms/two-column/types';
import type { IKlaviyoModal } from '@klaviyo-mkt/gatsby-components/organisms/modal/types';
import type { IComponentLayout } from '../../../components/component-loop';
import type { IPageBase } from '../types';

type TPageLikeContent = IPageBase & {
  components: IComponentLayout[];
  hero: IHero;
  includeHero: boolean;
  navigationColor?: 'charcoal' | 'lightSand';
  navigationStyle?: 'default' | 'minimal' | 'power_up' | null;
  shortTitle?: string;
  modals?: IKlaviyoModal[];
};

export interface IDefaultData {
  defaultPage: TPageLikeContent;
  cptFrontPage: TPageLikeContent;
  wpComparePage: TPageLikeContent;
  wpCorePage: TPageLikeContent;
  wpCustomerExperiencePage: TPageLikeContent;
  wpLandingPage: TPageLikeContent;
  wpMarketingResource: TPageLikeContent;
  wpPage: TPageLikeContent;
  wpPartnerPage: TPageLikeContent;
  wpPlatformIntegrationsPage: TPageLikeContent;
  wpPodcastPage: TPageLikeContent;
  wpProductPage: TPageLikeContent;
  wpProductUpdate: TPageLikeContent;
  wpSolutionsPage: TPageLikeContent;
  wpSuccessPage: TPageLikeContent;
}

export interface ICustomModalLayout {
  fullColumn: IFullColumn;
  twoColumn: ITwoColumn;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [x: string]: any;
}
const StyledLayout = styled.div``;

const JSONContainer = styled.div`
  padding-left: 7.2rem;
  padding-right: 7.2rem;
  margin: 0 auto;
  max-width: ${({ theme }) => `${theme.screenSize.s1366}rem`};
  overflow-x: scroll;
`;

const Empty = styled.div`
  height: 100vh;
`;

const noContent = (components: IComponentLayout[], hero: IHero) => {
  if (process.env.NODE_ENV === 'development') {
    return (
      <JSONContainer>
        <pre>{JSON.stringify(hero, null, 2)}</pre>
        <pre>{JSON.stringify(components, null, 2)}</pre>
      </JSONContainer>
    );
  }

  return (
    <Empty />
  );
};


const getComponent = (row: ICustomModalLayout) => {
  if (row?.fullColumn)
    return <FullColumn {...row?.fullColumn} />;

  if (row?.twoColumn)
    return <TwoColumn {...row.twoColumn} />;

  return null;
};

export const ModalComponentLoop = (components: any) => (
  components?.components?.modal?.templateCustomModal?.customModal?.customModal.map((row: ICustomModalLayout) => (
    <div key={JSON.stringify(row)}>
      {getComponent(row)}
    </div>
  ))
);

const Default = ({ components, hero, includeHero = true, modals }: { components: IComponentLayout[]; hero: IHero; includeHero?: boolean; modals?: any; }) => {
  const demoFormSettings = useDemoFormSettings();
  const modalStore = useModal();

  if (isEmpty(hero)) {
    return noContent(components, hero);
  }

  return (
    <StyledLayout>
      {(includeHero && (
        <EagerLoadingContextProvider eagerLoad={true}>
          <Hero
            {...hero}
            demoFormSettings={demoFormSettings?.default}
            pricingVariant={components.filter((row) => row?.componentType === 'PricingComponent')?.length > 0}
          />
        </EagerLoadingContextProvider>
      ))}
      <ComponentLoop components={components} />
      {modals && (
        modals.map((modal: IKlaviyoModal) => (
          <Modal
            backgroundColor={modal.backgroundColor}
            contentPadding="30"
            close={modalStore.close}
            show={modalStore.modalState.visibleModalId === `?modal=${modal.customModalId}`}
            size="constrained"
            isMediaModal
          > 
            <ModalComponentLoop components={modal}  />
          </Modal>
        )))}
    </StyledLayout>
  );
};

export default Default;
